/**
 * Manages the fixed-position layout that prevents iOS Safari from visually
 * shifting the chat UI when the software keyboard opens or closes.
 *
 * Only activates on mobile web (phone/tablet browser). On native and desktop
 * all returned values are inert and no side-effects run.
 */

import type React from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { type View as RNView } from 'react-native';
import {
    GRADIENT_COLOR_BOTTOM,
    GRADIENT_COLOR_TOP,
    isNonMobileWeb,
    isWeb,
} from 'smoothie';

// ─── Constants ───────────────────────────────────────────────────────────────

const MOBILE_WEB_HTML_GRADIENT = `linear-gradient(to bottom, ${GRADIENT_COLOR_TOP}, ${GRADIENT_COLOR_BOTTOM})`;

const MOBILE_WEB_DEFAULT_VIEWPORT =
    'width=device-width, initial-scale=1, shrink-to-fit=no';

const MOBILE_WEB_LOCKED_VIEWPORT =
    'width=device-width, initial-scale=1, shrink-to-fit=no, viewport-fit=cover, interactive-widget=overlays-content';

const MOBILE_WEB_SCOPED_STYLE_ID = 'chat-page-shell-gradient';

const MOBILE_WEB_SCOPED_CSS = `
html {
    background-image: ${MOBILE_WEB_HTML_GRADIENT};
    background-attachment: fixed;
    background-color: ${GRADIENT_COLOR_BOTTOM};
    min-height: 100lvh;
}
body {
    background-color: transparent;
    min-height: 100lvh;
}
#root {
    background-color: transparent;
}
`;

export const MOBILE_WEB_FIXED_GRADIENT_STYLE = {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    width: '100%' as const,
    zIndex: -1,
    pointerEvents: 'none' as const,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isMobileWebKeyboardOpen = (
    visualViewport: VisualViewport,
    initialViewportHeight: number,
): boolean => visualViewport.height < initialViewportHeight * 0.92;

const applyViewportOffsetTransform = (
    nodeRef: React.RefObject<RNView | null>,
    offsetTop: number,
): void => {
    const node = nodeRef.current as unknown as HTMLElement | null;
    if (node?.style == null) return;
    node.style.transition = 'none';
    node.style.transform =
        offsetTop > 0 ? `translate3d(0, ${offsetTop.toString()}px, 0)` : '';
    node.style.willChange = offsetTop > 0 ? 'transform' : '';
};

const isChatTextInput = (
    target: EventTarget | null,
): target is HTMLInputElement | HTMLTextAreaElement =>
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

const preventMobileWebPinchZoom = (event: TouchEvent): void => {
    if (event.touches.length > 1) event.preventDefault();
};

const preventMobileWebGestureZoom = (event: Event): void => {
    event.preventDefault();
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface MobileWebChatLockResult {
    /** True when running in a phone/tablet browser (locking is active). */
    isMobileWebLocked: boolean;
    /** True once the fixed layout is ready to render. */
    isLayoutReady: boolean;
    /** Height of the locked content area (= initial viewport height, no header). */
    contentHeight: number;
    /** Ref for the fixed-position container that holds chat content. */
    shellFixedRef: React.RefObject<RNView | null>;
    /** Ref for the gradient layer portalled into document.body. */
    gradientLayerRef: React.RefObject<RNView | null>;
    /** window.innerHeight captured at mount; used to size fixed containers. */
    initialViewportHeight: number;
    /** Bottom edge of the content area in viewport coordinates (for scroll anchoring). */
    mobileWebContainerBottom: number | null;
}

export const useMobileWebChatLock = (): MobileWebChatLockResult => {
    const isMobileWebLocked = isWeb && !isNonMobileWeb;

    const shellFixedRef = useRef<RNView>(null);
    const gradientLayerRef = useRef<RNView>(null);
    const viewportOffsetTopRef = useRef(0);
    const initialViewportHeightRef = useRef(
        typeof window !== 'undefined' ? window.innerHeight : 0,
    );
    const [isLayoutReady, setIsLayoutReady] = useState(false);

    const initialViewportHeight = initialViewportHeightRef.current;
    // No header means content always fills the full initial viewport height.
    const contentHeight = initialViewportHeight;
    const mobileWebContainerBottom =
        isMobileWebLocked && isLayoutReady ? initialViewportHeight : null;

    // Mark layout ready immediately — nothing to measure since there is no header.
    useLayoutEffect(() => {
        if (!isMobileWebLocked) return;
        setIsLayoutReady(true);
    }, [isMobileWebLocked]);

    const syncViewportCompensation = useCallback(() => {
        if (typeof window === 'undefined') return;
        const visualViewport = window.visualViewport;
        if (visualViewport == null) return;

        const offsetTop = isMobileWebKeyboardOpen(
            visualViewport,
            initialViewportHeight,
        )
            ? visualViewport.offsetTop
            : 0;

        viewportOffsetTopRef.current = offsetTop;
        applyViewportOffsetTransform(shellFixedRef, offsetTop);
        applyViewportOffsetTransform(gradientLayerRef, offsetTop);
    }, [initialViewportHeight]);

    // Listen for visual viewport changes (keyboard open/close, scroll).
    useLayoutEffect(() => {
        if (
            !isMobileWebLocked ||
            !isLayoutReady ||
            typeof window === 'undefined'
        ) {
            return undefined;
        }
        const visualViewport = window.visualViewport;
        if (visualViewport == null) return undefined;

        const onChange = () => {
            syncViewportCompensation();
        };
        const onTouchStart = () => {
            syncViewportCompensation();
        };

        visualViewport.addEventListener('scroll', onChange);
        visualViewport.addEventListener('resize', onChange);
        document.addEventListener('touchstart', onTouchStart, true);
        syncViewportCompensation();

        return () => {
            visualViewport.removeEventListener('scroll', onChange);
            visualViewport.removeEventListener('resize', onChange);
            document.removeEventListener('touchstart', onTouchStart, true);
            viewportOffsetTopRef.current = 0;
            applyViewportOffsetTransform(shellFixedRef, 0);
            applyViewportOffsetTransform(gradientLayerRef, 0);
        };
    }, [isMobileWebLocked, isLayoutReady, syncViewportCompensation]);

    // Re-sync compensation on every render while a keyboard offset is active.
    useLayoutEffect(() => {
        if (
            !isMobileWebLocked ||
            !isLayoutReady ||
            viewportOffsetTopRef.current <= 0
        )
            return;
        syncViewportCompensation();
    });

    // Lock body/html styles and viewport meta to prevent iOS Safari layout shifts.
    useLayoutEffect(() => {
        if (!isMobileWebLocked || typeof document === 'undefined')
            return undefined;

        const html = document.documentElement;
        const body = document.body;
        const prev = {
            htmlMinHeight: html.style.minHeight,
            htmlBgImage: html.style.backgroundImage,
            htmlBgAttachment: html.style.backgroundAttachment,
            htmlBgColor: html.style.backgroundColor,
            bodyHeight: body.style.height,
            bodyOverflow: body.style.overflow,
            bodyPosition: body.style.position,
            bodyWidth: body.style.width,
            bodyTop: body.style.top,
            bodyLeft: body.style.left,
            bodyBgColor: body.style.backgroundColor,
        };

        html.style.minHeight = '100lvh';
        html.style.backgroundImage = MOBILE_WEB_HTML_GRADIENT;
        html.style.backgroundAttachment = 'fixed';
        html.style.backgroundColor = GRADIENT_COLOR_BOTTOM;
        body.style.height = `${initialViewportHeight.toString()}px`;
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.width = '100%';
        body.style.top = '0';
        body.style.left = '0';
        body.style.backgroundColor = 'transparent';

        let scopedStyleNode = document.getElementById(
            MOBILE_WEB_SCOPED_STYLE_ID,
        );
        const createdScopedStyleNode = scopedStyleNode == null;
        if (createdScopedStyleNode) {
            scopedStyleNode = document.createElement('style');
            scopedStyleNode.id = MOBILE_WEB_SCOPED_STYLE_ID;
            scopedStyleNode.textContent = MOBILE_WEB_SCOPED_CSS;
            document.head.appendChild(scopedStyleNode);
        }

        const viewportMetas = document.querySelectorAll(
            'meta[name="viewport"]',
        );
        viewportMetas.forEach((meta) => {
            meta.setAttribute('content', MOBILE_WEB_LOCKED_VIEWPORT);
        });

        const handleTouchStart = (event: TouchEvent) => {
            if (event.touches.length > 1) {
                event.preventDefault();
                return;
            }
            // Focus the chat input ahead of the browser's own focus handling,
            // with preventScroll so iOS Safari doesn't scroll/pan it into view.
            // We do NOT preventDefault so native caret positioning keeps working.
            if (isChatTextInput(event.target)) {
                event.target.focus({ preventScroll: true });
            }
        };

        document.addEventListener('touchstart', handleTouchStart, {
            capture: true,
            passive: false,
        });
        document.addEventListener('touchmove', preventMobileWebPinchZoom, {
            capture: true,
            passive: false,
        });
        document.addEventListener('gesturestart', preventMobileWebGestureZoom, {
            capture: true,
            passive: false,
        });
        document.addEventListener(
            'gesturechange',
            preventMobileWebGestureZoom,
            { capture: true, passive: false },
        );
        document.addEventListener('gestureend', preventMobileWebGestureZoom, {
            capture: true,
            passive: false,
        });

        return () => {
            document.removeEventListener('touchstart', handleTouchStart, true);
            document.removeEventListener(
                'touchmove',
                preventMobileWebPinchZoom,
                true,
            );
            document.removeEventListener(
                'gesturestart',
                preventMobileWebGestureZoom,
                true,
            );
            document.removeEventListener(
                'gesturechange',
                preventMobileWebGestureZoom,
                true,
            );
            document.removeEventListener(
                'gestureend',
                preventMobileWebGestureZoom,
                true,
            );

            html.style.minHeight = prev.htmlMinHeight;
            html.style.backgroundImage = prev.htmlBgImage;
            html.style.backgroundAttachment = prev.htmlBgAttachment;
            html.style.backgroundColor = prev.htmlBgColor;
            body.style.height = prev.bodyHeight;
            body.style.overflow = prev.bodyOverflow;
            body.style.position = prev.bodyPosition;
            body.style.width = prev.bodyWidth;
            body.style.top = prev.bodyTop;
            body.style.left = prev.bodyLeft;
            body.style.backgroundColor = prev.bodyBgColor;

            if (createdScopedStyleNode && scopedStyleNode?.parentNode != null) {
                scopedStyleNode.parentNode.removeChild(scopedStyleNode);
            }
            viewportMetas.forEach((meta) => {
                meta.setAttribute('content', MOBILE_WEB_DEFAULT_VIEWPORT);
            });
        };
    }, [isMobileWebLocked, initialViewportHeight]);

    // Keep the gradient layer sized to the full viewport as it resizes.
    useLayoutEffect(() => {
        if (!isMobileWebLocked || typeof document === 'undefined')
            return undefined;

        const gradientNode =
            gradientLayerRef.current as unknown as HTMLElement | null;
        const rootNode = document.getElementById('root');
        const previousRootBackground = rootNode?.style.backgroundColor ?? '';

        const syncGradientLayerSize = () => {
            if (gradientNode?.style == null) return;
            gradientNode.style.top = '0';
            gradientNode.style.bottom = '0';
            gradientNode.style.height = '100lvh';
            gradientNode.style.minHeight = '100%';
        };

        if (rootNode != null) {
            rootNode.style.backgroundColor = 'transparent';
        }

        syncGradientLayerSize();
        window.visualViewport?.addEventListener(
            'resize',
            syncGradientLayerSize,
        );

        return () => {
            window.visualViewport?.removeEventListener(
                'resize',
                syncGradientLayerSize,
            );
            if (rootNode != null) {
                rootNode.style.backgroundColor = previousRootBackground;
            }
        };
    }, [isMobileWebLocked]);

    return {
        isMobileWebLocked,
        isLayoutReady,
        contentHeight,
        shellFixedRef,
        gradientLayerRef,
        initialViewportHeight,
        mobileWebContainerBottom,
    };
};
